import { useState, useEffect, useCallback, useRef, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2 } from "lucide-react";
import { ArtifactDownloadModal } from "./ArtifactDownloadModal";
import {
  ChatViewRiskAssessmentReport,
  type ChatViewRiskAssessmentReportProps,
} from "./ChatViewRiskAssessmentReport";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";
import {
  normalizeProjectSourceReferences,
  type ProjectSourceReference,
} from "@/lib/projectSourceReferences";
import { ArtifactFileViewer } from "@/components/artifacts/ArtifactFileViewer";
import { TbmRecordReport, TbmScenarioReport } from "./TbmArtifactReport";
import {
  normalizeRiskAssessmentListValue,
  parseRiskAssessmentListValue,
} from "./riskAssessmentListValues";
import {
  RiskAssessmentSurveyEditEntry,
  RiskAssessmentSurveyLoadRetry,
  RiskAssessmentSurveyPanel,
  useRiskAssessmentSurvey,
} from "./RiskAssessmentSurvey";
import { useRiskAssessmentSurveyEnabled } from "@/components/ProductSurfaceLockConsole";

// 아티팩트 내 이미지 소스: 외부 URL(사고사례 등)과 api-core가 보관 중인 사용자 업로드 파일(fileId) 모두 지원
type ArtifactImg = { kind: 'url'; url: string } | { kind: 'file'; fileId: string };
export type SourceDocumentAttachment = {
  fileId: string;
  kind: 'source_document';
  name?: string;
  mimeType?: string;
  size?: number;
  format?: string;
  characterCount?: number;
};

export const normalizeSourceDocumentAttachments = (values: unknown): SourceDocumentAttachment[] => {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const normalized: SourceDocumentAttachment[] = [];
  for (const value of values) {
    if (!value || typeof value !== 'object') continue;
    const item = value as Record<string, unknown>;
    const fileId = item.fileId ?? item.file_id ?? item.id;
    const kind = item.kind ?? item.attachmentKind ?? item.attachment_kind;
    if (kind !== 'source_document' || (typeof fileId !== 'string' && typeof fileId !== 'number')) continue;
    const id = String(fileId);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    normalized.push({
      fileId: id,
      kind: 'source_document',
      name: typeof item.name === 'string' ? item.name : typeof item.originalName === 'string' ? item.originalName : undefined,
      mimeType: typeof item.mimeType === 'string' ? item.mimeType : undefined,
      size: typeof item.size === 'number' ? item.size : undefined,
      format: typeof item.format === 'string' ? item.format : undefined,
      characterCount: typeof item.characterCount === 'number' ? item.characterCount : undefined,
    });
  }
  return normalized;
};
type RiskFrequencySeverityMethod = '3x3' | '4x3' | '5x4';

const normalizeAttachmentFileIds = (ids: Array<string | number | null | undefined> = []): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const raw of ids) {
    let id = String(raw ?? '').trim().replace(/^file:/i, '').trim();
    id = id.replace(/^['"]|['"]$/g, '');
    if (!id) continue;

    const lowered = id.replace(/\s+/g, ' ').toLowerCase();
    if (
      lowered === 'id' ||
      lowered === 'example' ||
      lowered === '예시' ||
      lowered.includes('파일') ||
      /^uuid\d*$/.test(lowered)
    ) {
      continue;
    }
    if (!/^[a-z0-9_-]+$/i.test(id)) continue;
    if (seen.has(id)) continue;

    seen.add(id);
    normalized.push(id);
  }

  return normalized;
};

// /api/files/:id 는 Bearer 토큰이 필요해 <img src>만으로는 못 불러옴. fetch→blob URL로 렌더.
export const AuthImg = ({ fileId, className, alt, onError }: { fileId: string; className?: string; alt?: string; onError?: () => void }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setSrc(null);
    setFailed(false);

    if (!fileId) {
      setFailed(true);
      onError?.();
      return () => { cancelled = true; };
    }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    fetchWithAuth(`${apiUrl}/api/files/${fileId}/content`, { skipRedirectOn401: true })
      .then(res => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setSrc(createdUrl);
      })
      .catch(() => { if (!cancelled) { setFailed(true); onError?.(); } });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fileId, onError]);

  if (failed) {
    return <div className={`${className ?? ''} flex items-center justify-center bg-gray-200 dark:bg-gray-800 text-[10px] text-gray-500`}>로드 실패</div>;
  }
  if (!src) {
    return <div className={`${className ?? ''} bg-gray-200 dark:bg-gray-800 animate-pulse`} />;
  }
  return <img src={src} className={className} alt={alt ?? ''} />;
};

const readableFileSize = (bytes?: number) => {
  if (!bytes || bytes < 1) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)}MB`;
};

const documentExtension = (name?: string) => name?.toLowerCase().split('.').pop() ?? '';

const ArtifactSourceDocumentCard = ({ documentFile }: { documentFile: SourceDocumentAttachment }) => {
  const [metadata, setMetadata] = useState(documentFile);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

  useEffect(() => {
    if (documentFile.name && documentFile.size !== undefined) return;
    let cancelled = false;
    fetchWithAuth(`${apiUrl}/api/files/${documentFile.fileId}`, { skipRedirectOn401: true })
      .then(async response => response.ok ? response.json() : null)
      .then(payload => {
        if (!payload || cancelled) return;
        const unwrapped = unwrapApiData<unknown>(payload);
        if (!unwrapped || typeof unwrapped !== 'object') return;
        const record = unwrapped as Record<string, unknown>;
        const source = record.file && typeof record.file === 'object' ? record.file as Record<string, unknown> : record;
        const extraction = source.extraction && typeof source.extraction === 'object' ? source.extraction as Record<string, unknown> : {};
        const rawSize = source.size ?? source.fileSize ?? source.file_size;
        const normalizedSize = typeof rawSize === 'number'
          ? rawSize
          : typeof rawSize === 'string' && Number.isFinite(Number(rawSize)) ? Number(rawSize) : undefined;
        setMetadata(previous => ({
          ...previous,
          name: typeof source.originalName === 'string' ? source.originalName : typeof source.name === 'string' ? source.name : previous.name,
          mimeType: typeof source.mimeType === 'string' ? source.mimeType : previous.mimeType,
          size: normalizedSize ?? previous.size,
          format: typeof source.format === 'string' ? source.format : typeof extraction.format === 'string' ? extraction.format : previous.format,
          characterCount: typeof source.characterCount === 'number'
            ? source.characterCount
            : typeof extraction.characterCount === 'number' ? extraction.characterCount : previous.characterCount,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apiUrl, documentFile.fileId, documentFile.name, documentFile.size]);

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetchWithAuth(`${apiUrl}/api/files/${documentFile.fileId}/content`);
      if (!response.ok) throw new Error(`download failed (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = metadata.name || 'source-document';
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      alert('문서를 다운로드하지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="flex min-w-0 items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-2 dark:border-[#2a2a2e] dark:bg-[#19191b]">
        <button
          type="button"
          onClick={() => setIsViewerOpen(true)}
          className="group flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1 py-0.5 text-left"
          aria-label={`${metadata.name || '참고 문서'} 열기`}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-seexr-primary transition-colors group-hover:bg-seexr-solid/15">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[11px] font-bold text-gray-800 transition-colors group-hover:text-seexr-primary dark:text-gray-200">{metadata.name || '참고 문서'}</span>
            <span className="mt-0.5 block text-[9px] font-semibold text-gray-400">{[metadata.format?.toUpperCase() || documentExtension(metadata.name).toUpperCase(), readableFileSize(metadata.size), '분석 완료'].filter(Boolean).join(' · ')}</span>
          </span>
        </button>
        <button type="button" onClick={download} disabled={isDownloading} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-seexr-primary disabled:opacity-50 dark:hover:bg-[#2a2a2e]" aria-label={`${metadata.name || '문서'} 다운로드`}>
          {isDownloading
            ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
        </button>
      </div>
      {isViewerOpen && (
        <ArtifactFileViewer
          file={{
            fileId: metadata.fileId,
            name: metadata.name || '참고 문서',
            mimeType: metadata.mimeType,
            size: metadata.size,
          }}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </>
  );
};


type SessionArtifactOption = {
  key: string;
  title: string;
  type: string;
};

export type RiskAssessmentReportComponent = ComponentType<ChatViewRiskAssessmentReportProps>;

type ArtifactCanvasProps = {
  type: string;
  title: string;
  xmlContent: string;
  artifactId?: string | number | null;
  sessionId?: string | null;
  messageId?: string | number | null;
  initialRiskAcknowledgedAt?: string | null;
  initialTbmAcknowledgedAt?: string | null;
  onAcknowledgementChange?: (value: {
    artifactId: string | null;
    type: string;
    acknowledgedAt: string | null;
  }) => void;
  onClose: () => void;
  showCloseButton?: boolean;
  closeButtonClassName?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onSave?: (newXml: string, newTitle: string) => void | Promise<void>;
  fallbackAttachmentFileIds?: string[];
  sourceDocuments?: SourceDocumentAttachment[];
  projectSourceReferences?: ProjectSourceReference[];
  sessionArtifacts?: SessionArtifactOption[];
  activeArtifactKey?: string;
  onSelectArtifact?: (key: string) => void;
  RiskAssessmentReport?: RiskAssessmentReportComponent;
  contentWidthClassName?: string;
};

function RiskAcknowledgementHelp() {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const updateAnchorRect = useCallback(() => {
    if (triggerRef.current) setAnchorRect(triggerRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!anchorRect) return;
    const update = () => updateAnchorRect();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRect, updateAnchorRect]);

  const tooltipWidth = 288;
  const tooltipLeft = anchorRect
    ? Math.min(
        Math.max(anchorRect.left + anchorRect.width / 2, tooltipWidth / 2 + 12),
        window.innerWidth - tooltipWidth / 2 - 12,
      )
    : 0;
  const showBelow = !!anchorRect && anchorRect.top < window.innerHeight / 2;

  return (
    <span className="mt-0.5 inline-flex shrink-0 align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label="체크 해제 안내"
        onMouseEnter={updateAnchorRect}
        onMouseLeave={() => setAnchorRect(null)}
        onFocus={updateAnchorRect}
        onBlur={() => setAnchorRect(null)}
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-gray-500 outline-none transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 dark:border-gray-600 dark:bg-gray-800/40 dark:text-gray-400 dark:hover:bg-gray-800/70"
      >
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M4.7 4.6a1.35 1.35 0 1 1 2.15 1.09c-.55.4-.85.68-.85 1.31M6 8.9h.01" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
        </svg>
      </button>
      {anchorRect && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          className="pointer-events-none fixed w-72 whitespace-normal rounded-lg border border-gray-200 bg-white p-3 text-left text-[11px] font-medium leading-relaxed text-gray-700 opacity-100 shadow-xl dark:border-gray-700 dark:bg-[#202023] dark:text-gray-200"
          style={{
            left: tooltipLeft,
            top: showBelow ? anchorRect.bottom + 8 : anchorRect.top - 8,
            maxWidth: 'calc(100vw - 24px)',
            transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
            zIndex: 2147483647,
          }}
        >
          <span className="mb-1.5 block font-black text-gray-800 dark:text-gray-100">열람 동의 안내</span>
          체크를 해제하면 보고서가 다시 가려지며, 다시 열람하려면 안내 내용에 재동의해야 합니다.
        </div>,
        document.body,
      )}
    </span>
  );
}

export function ArtifactCanvas({
  type,
  title,
  xmlContent,
  artifactId,
  sessionId,
  messageId,
  initialRiskAcknowledgedAt,
  initialTbmAcknowledgedAt,
  onAcknowledgementChange,
  onClose,
  showCloseButton = true,
  closeButtonClassName,
  isExpanded,
  onToggleExpand,
  onSave,
  fallbackAttachmentFileIds,
  sourceDocuments = [],
  projectSourceReferences: initialProjectSourceReferences,
  sessionArtifacts = [],
  activeArtifactKey,
  onSelectArtifact,
  RiskAssessmentReport = ChatViewRiskAssessmentReport,
  contentWidthClassName,
}: ArtifactCanvasProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [viewerState, setViewerState] = useState<{ images: ArtifactImg[]; index: number } | null>(null);
  const [editItems, setEditItems] = useState<Array<{
    id: string; prob_freq: string; severity: string;
    hazard_primary_category: string; risk_category: string; accident_classification: string;
    hazard: string; risk_level: string; reduction_measures_detail: string[];
    post_improvement_prob_freq: string; post_improvement_severity: string;
    post_improvement_risk_level: string;
    basis: string; legal_basis_details: string; check_result: string; statistical_basis: string;
    recommended_prob_freq: string; recommended_severity: string;
    recommended_risk_level: string;
  }>>([]);
  const [riskMethodType, setRiskMethodType] = useState<'빈도강도법' | '3단계 판단법' | '체크리스트법'>('빈도강도법');
  const [riskMethod, setRiskMethod] = useState<RiskFrequencySeverityMethod>('3x3');
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  const [editEquipment, setEditEquipment] = useState<string[]>([]);
  const [editMaterial, setEditMaterial] = useState<string[]>([]);
  const [eqInput, setEqInput] = useState('');
  const [matInput, setMatInput] = useState('');
  const [riskHeaderActionsTarget, setRiskHeaderActionsTarget] =
    useState<HTMLElement | null>(null);

  // cases / laws 편집 상태 — 행별로 텍스트 필드 수정. risk_assessment와 같은 패턴.
  // cases.images: 기존 외부 URL + 새로 업로드한 파일은 "file:{uuid}" 프리픽스로 저장
  const [editCases, setEditCases] = useState<Array<{
    title: string; occurredAt: string; accidentLocation: string;
    accidentDetails: string; accidentCause: string; specificCause: string;
    preventiveMeasure: string; images: string[];
  }>>([]);
  const [editLaws, setEditLaws] = useState<Array<{
    act: string; article: string; detail: string; paragraph: string;
    violation_risk: string; download_url: string;
  }>>([]);
  // risk_assessment의 첨부 이미지 파일 ID 편집 상태
  const [editAttachmentFileIds, setEditAttachmentFileIds] = useState<string[]>([]);

  // 다운로드 팝오버 — 열려있으면 anchorRect 보관, 닫히면 null
  const [downloadAnchorRect, setDownloadAnchorRect] = useState<DOMRect | null>(null);
  const downloadBtnRef = useRef<HTMLButtonElement | null>(null);
  const [isArtifactSwitcherOpen, setIsArtifactSwitcherOpen] = useState(false);
  const artifactSwitcherRef = useRef<HTMLDivElement | null>(null);
  const isTbmArtifact = type === 'tbm_scenario' || type === 'tbm_record';
  const canEditArtifact = ['risk_assessment', 'tbm_scenario', 'tbm_record', 'cases', 'laws', 'legal_search', 'accident_search'].includes(type);
  const initialAcknowledgedAt = isTbmArtifact
    ? initialTbmAcknowledgedAt
    : initialRiskAcknowledgedAt;
  const requiresRiskAcknowledgement = (type === 'risk_assessment' || isTbmArtifact) && !!xmlContent.trim();
  const [resolvedRiskArtifactId, setResolvedRiskArtifactId] = useState<string | null>(
    artifactId === undefined || artifactId === null ? null : String(artifactId),
  );
  const [projectSourceReferences, setProjectSourceReferences] = useState<ProjectSourceReference[]>(
    initialProjectSourceReferences ?? [],
  );
  const [riskAcknowledgedAt, setRiskAcknowledgedAt] = useState<string | null>(
    initialAcknowledgedAt ?? null,
  );
  const [riskAcknowledgementLoading, setRiskAcknowledgementLoading] = useState(
    requiresRiskAcknowledgement && initialAcknowledgedAt === undefined,
  );
  const [riskAcknowledgementSaving, setRiskAcknowledgementSaving] = useState(false);
  const [riskAcknowledgementError, setRiskAcknowledgementError] = useState<string | null>(null);
  const [riskConsentChecked, setRiskConsentChecked] = useState(false);
  const automaticRiskAcknowledgementLookupRef = useRef<string | null>(null);
  const canViewRiskArtifact = !requiresRiskAcknowledgement || (!!riskAcknowledgedAt && !riskAcknowledgementLoading);
  const artifactContentWidthClassName = isExpanded
    ? 'max-w-none'
    : contentWidthClassName ?? 'max-w-2xl';
  const isSurveyEnabled = useRiskAssessmentSurveyEnabled();
  const riskAssessmentSurvey = useRiskAssessmentSurvey({
    artifactId: resolvedRiskArtifactId,
    enabled:
      isSurveyEnabled &&
      type === 'risk_assessment' &&
      canViewRiskArtifact &&
      !!xmlContent.trim(),
    canAutoOpen: !isEditing,
    riskMethodType,
  });

  useEffect(() => {
    if (initialProjectSourceReferences !== undefined) {
      setProjectSourceReferences(initialProjectSourceReferences);
    }
  }, [initialProjectSourceReferences]);

  useEffect(() => {
    if (
      type !== 'risk_assessment' ||
      initialProjectSourceReferences !== undefined ||
      !resolvedRiskArtifactId
    ) {
      return;
    }
    let cancelled = false;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    fetchWithAuth(
      `${apiUrl}/api/artifacts/${encodeURIComponent(resolvedRiskArtifactId)}`,
      { skipRedirectOn401: true },
    )
      .then(async response => (response.ok ? response.json() : null))
      .then(payload => {
        if (!payload || cancelled) return;
        const artifact = unwrapApiData<Record<string, unknown>>(payload);
        const metadata =
          artifact?.metadata && typeof artifact.metadata === 'object'
            ? (artifact.metadata as Record<string, unknown>)
            : {};
        setProjectSourceReferences(
          normalizeProjectSourceReferences(metadata.project_source_references),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [initialProjectSourceReferences, resolvedRiskArtifactId, type]);

  const loadRiskAcknowledgement = useCallback(async () => {
    if (!requiresRiskAcknowledgement) return;
    const query = new URLSearchParams();
    if (artifactId !== undefined && artifactId !== null && String(artifactId).trim()) {
      query.set('artifactId', String(artifactId));
    } else if (sessionId && messageId !== undefined && messageId !== null && String(messageId).trim()) {
      query.set('sessionId', sessionId);
      query.set('messageId', String(messageId));
      if (isTbmArtifact) query.set('kind', type === 'tbm_scenario' ? 'scenario' : 'record');
    } else {
      setRiskAcknowledgementLoading(false);
      setRiskAcknowledgementError('평가 저장을 완료한 뒤 열람할 수 있습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    setRiskAcknowledgementLoading(true);
    setRiskAcknowledgementError(null);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
      const endpointUrl = isTbmArtifact
        ? `${apiUrl}/api/artifacts/tbm-acknowledgement`
        : `${apiUrl}/api/artifacts/risk-acknowledgement`;
      const response = await fetchWithAuth(`${endpointUrl}?${query.toString()}`, {
        credentials: 'include',
        skipRedirectOn401: false,
      });
      if (!response.ok) {
        setRiskAcknowledgementError(
          response.status === 404
            ? '평가 결과 저장을 확인하지 못했습니다. 평가를 다시 실행해 주세요.'
            : '열람 동의 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        );
        return;
      }
      const data = unwrapApiData<Record<string, unknown>>(await response.json());
      const acknowledgedAt = data?.acknowledgedAt;
      const nextArtifactId = data?.artifactId;
      setResolvedRiskArtifactId(
        typeof nextArtifactId === 'string' || typeof nextArtifactId === 'number'
          ? String(nextArtifactId)
          : null,
      );
      setRiskAcknowledgedAt(typeof acknowledgedAt === 'string' && acknowledgedAt ? acknowledgedAt : null);
      onAcknowledgementChange?.({
        artifactId: typeof nextArtifactId === 'string' || typeof nextArtifactId === 'number' ? String(nextArtifactId) : null,
        type,
        acknowledgedAt: typeof acknowledgedAt === 'string' && acknowledgedAt ? acknowledgedAt : null,
      });
    } catch (error) {
      console.warn('[ArtifactCanvas] risk acknowledgement lookup failed:', error);
      setRiskAcknowledgementError('열람 동의 상태를 불러오지 못했습니다. 네트워크 상태를 확인해 주세요.');
    } finally {
      setRiskAcknowledgementLoading(false);
    }
  }, [artifactId, isTbmArtifact, messageId, onAcknowledgementChange, requiresRiskAcknowledgement, sessionId, type]);

  useEffect(() => {
    if (initialAcknowledgedAt !== undefined || !requiresRiskAcknowledgement) return;
    const lookupKey =
      artifactId !== undefined && artifactId !== null && String(artifactId).trim()
        ? `artifact:${String(artifactId)}`
        : sessionId && messageId !== undefined && messageId !== null && String(messageId).trim()
          ? `message:${sessionId}:${String(messageId)}:${type}`
          : null;
    if (!lookupKey || automaticRiskAcknowledgementLookupRef.current === lookupKey) return;
    automaticRiskAcknowledgementLookupRef.current = lookupKey;
    void loadRiskAcknowledgement();
  }, [artifactId, initialAcknowledgedAt, loadRiskAcknowledgement, messageId, requiresRiskAcknowledgement, sessionId, type]);

  const updateRiskAcknowledgement = async (acknowledged: boolean) => {
    if (riskAcknowledgementSaving) return;
    const reference = resolvedRiskArtifactId
      ? { artifactId: resolvedRiskArtifactId }
      : sessionId && messageId !== undefined && messageId !== null
        ? { sessionId, messageId: String(messageId) }
        : artifactId !== undefined && artifactId !== null
          ? { artifactId: String(artifactId) }
          : null;
    if (!reference) {
      setRiskAcknowledgementError('평가 저장을 완료한 뒤 열람할 수 있습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    setRiskAcknowledgementSaving(true);
    setRiskAcknowledgementError(null);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
      const endpointUrl = isTbmArtifact
        ? `${apiUrl}/api/artifacts/tbm-acknowledgement`
        : `${apiUrl}/api/artifacts/risk-acknowledgement`;
      const response = await fetchWithAuth(endpointUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        skipRedirectOn401: false,
        body: JSON.stringify({
          ...reference,
          ...(isTbmArtifact ? { kind: type === 'tbm_scenario' ? 'scenario' : 'record' } : {}),
          acknowledged,
        }),
      });
      if (!response.ok) throw new Error(`Risk acknowledgement update failed (${response.status})`);
      const data = unwrapApiData<Record<string, unknown>>(await response.json());
      const acknowledgedAt = data?.acknowledgedAt;
      const nextArtifactId = data?.artifactId;
      if (typeof nextArtifactId === 'string' || typeof nextArtifactId === 'number') {
        setResolvedRiskArtifactId(String(nextArtifactId));
      }
      setRiskAcknowledgedAt(typeof acknowledgedAt === 'string' && acknowledgedAt ? acknowledgedAt : null);
      onAcknowledgementChange?.({
        artifactId: typeof nextArtifactId === 'string' || typeof nextArtifactId === 'number'
          ? String(nextArtifactId)
          : resolvedRiskArtifactId,
        type,
        acknowledgedAt: typeof acknowledgedAt === 'string' && acknowledgedAt ? acknowledgedAt : null,
      });
      setRiskConsentChecked(false);
      if (!acknowledged) {
        setIsEditing(false);
        setDownloadAnchorRect(null);
        setViewerState(null);
      }
    } catch (error) {
      console.error('[ArtifactCanvas] risk acknowledgement update failed:', error);
      setRiskAcknowledgementError('열람 동의 상태를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setRiskAcknowledgementSaving(false);
    }
  };

  useEffect(() => {
    if (!isArtifactSwitcherOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!artifactSwitcherRef.current?.contains(event.target as Node)) {
        setIsArtifactSwitcherOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsArtifactSwitcherOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isArtifactSwitcherOpen]);

  // 편집 중 파일 업로드 헬퍼 — /api/files에 multipart 업로드하고 fileId 반환
  const uploadToFiles = async (file: File): Promise<string | undefined> => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentKind', 'site_photo');
    try {
      const res = await fetchWithAuth(`${apiUrl}/api/files`, { method: 'POST', body: fd });
      if (!res.ok) return undefined;
      const json = await res.json();
      const unwrapped = unwrapApiData<unknown>(json);
      if (!unwrapped || typeof unwrapped !== 'object') return undefined;
      const record = unwrapped as Record<string, unknown>;
      const nestedFile = record.file && typeof record.file === 'object' ? record.file as Record<string, unknown> : undefined;
      const fileId = record.fileId ?? record.id ?? nestedFile?.id;
      return typeof fileId === 'string' || typeof fileId === 'number' ? String(fileId) : undefined;
    } catch (e) {
      console.error('[ArtifactCanvas] upload failed:', e);
      return undefined;
    }
  };

  const handleDownload = useCallback(async () => {
    if (!viewerState) return;
    const cur = viewerState.images[viewerState.index];
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    try {
      let res: Response;
      if (cur.kind === 'url') {
        res = await fetch(cur.url);
      } else {
        res = await fetchWithAuth(`${apiUrl}/api/files/${cur.fileId}/content`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `image-${viewerState.index + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      if (cur.kind === 'url') window.open(cur.url, "_blank");
    }
  }, [viewerState]);

  useEffect(() => {
    if (!viewerState) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setViewerState(null);
      } else if (e.key === "ArrowLeft") {
        setViewerState((prev) => prev && prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev);
      } else if (e.key === "ArrowRight") {
        setViewerState((prev) => prev && prev.index < prev.images.length - 1 ? { ...prev, index: prev.index + 1 } : prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewerState]);

  const bulletLines = (text: string) => {
    if (!text) return '';
    const liMatches = [...text.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
    if (liMatches.length > 0)
      return liMatches.map(m => `• ${m[1].trim()}`).join('\n');
    return text.split('\n').map(s => s.trim()).filter(Boolean).map(s => `• ${s}`).join('\n');
  };

  const parseBullets = (text: string): string[] => {
    if (!text) return [''];
    const li = [...text.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
    if (li.length > 0) return li.map(m => m[1].trim()).filter(Boolean);
    const lines = text.split('\n').map(s => s.trim()).filter(Boolean);
    return lines.length > 0 ? lines : [''];
  };

  const serializeBullets = (items: string[]): string => {
    const filled = items.map(s => s.trim()).filter(Boolean);
    if (filled.length === 0) return '';
    if (filled.length === 1) return filled[0];
    return `<ul>${filled.map(s => `<li>${s}</li>`).join('')}</ul>`;
  };

  const parseXmlString = (xmlString: string) => {
    // Very simple xml regex parser for flat tags
    const extract = (tag: string, source: string) => {
      const match = source.match(new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)</${tag}>`, 'i'));
      return match ? match[1].trim() : '';
    };
    const extractAll = (tag: string, source: string) => {
      const matches = [...source.matchAll(new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)</${tag}>`, 'gi'))];
      return matches.map(m => m[1]);
    };
    return { extract, extractAll };
  };

  const getRiskAttachmentFileIds = (source: string) => {
    const { extract, extractAll } = parseXmlString(`<root>${source}</root>`);
    const block = extract('attachment_file_ids', source);
    const xmlIds = block ? extractAll('id', block) : [];
    return normalizeAttachmentFileIds([...xmlIds, ...(fallbackAttachmentFileIds ?? [])]);
  };

  const withRiskAttachmentFileIds = (source: string) => {
    if (type !== 'risk_assessment') return source;

    const attachmentFileIds = getRiskAttachmentFileIds(source);
    if (attachmentFileIds.length === 0) return source;

    const block = `<attachment_file_ids>\n${attachmentFileIds.map(id => `  <id>${id}</id>`).join('\n')}\n</attachment_file_ids>`;
    if (/<attachment_file_ids\b/i.test(source)) {
      return source.replace(/<attachment_file_ids\b[^>]*>[\s\S]*?<\/attachment_file_ids>/i, block);
    }

    const itemsIndex = source.search(/<items\b/i);
    if (itemsIndex >= 0) {
      return `${source.slice(0, itemsIndex)}${block}\n${source.slice(itemsIndex)}`;
    }

    return `${block}\n${source}`;
  };

  // Close method picker on outside click
  useEffect(() => {
    if (!showMethodPicker) return;
    const close = () => setShowMethodPicker(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showMethodPicker]);

  // Detect risk method type from <method> metadata tag
  useEffect(() => {
    if (type !== 'risk_assessment') return;
    const { extract } = parseXmlString(`<root>${xmlContent}</root>`);
    const method = extract('method', xmlContent);

    if (method.startsWith('빈도강도법')) {
      setRiskMethodType('빈도강도법');
      if (method.includes('5x4')) setRiskMethod('5x4');
      else if (method.includes('4x3')) setRiskMethod('4x3');
      else setRiskMethod('3x3');
    } else if (method === '3단계 판단법') {
      setRiskMethodType('3단계 판단법');
    } else if (method === '체크리스트법') {
      setRiskMethodType('체크리스트법');
    } else {
      // Fallback: detect from item tags for legacy data without <method>
      const itemsBlock = extract('items', xmlContent) || xmlContent;
      const { extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
      const rawItems = extractAll('item', itemsBlock);
      if (rawItems.length > 0) {
        const { extract: e } = parseXmlString(rawItems[0]);
        if (e('prob_freq', rawItems[0])) {
          setRiskMethodType('빈도강도법');
          let mp = 0, ms = 0;
          rawItems.forEach(s => {
            const { extract: ex } = parseXmlString(s);
            const p = Number(ex('prob_freq', s));
            const sv = Number(ex('severity', s));
            if (!isNaN(p) && p > mp) mp = p;
            if (!isNaN(sv) && sv > ms) ms = sv;
          });
          if (ms > 3 || mp > 4) setRiskMethod('5x4');
          else if (mp > 3) setRiskMethod('4x3');
          else setRiskMethod('3x3');
        } else if (e('check_result', rawItems[0]) !== undefined || /는가\?|었는가\?|인가\?|\?$/.test((e('hazard_analysis', rawItems[0]) || '').trim())) {
          setRiskMethodType('체크리스트법');
        } else {
          setRiskMethodType('3단계 판단법');
        }
      }
    }
  }, [xmlContent, type]);

  const dragRef = useRef<{ fromIdx: number; toIdx: number } | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const startEditing = () => {
    setIsArtifactSwitcherOpen(false);
    setEditTitle(title);
    if (type === 'tbm_scenario' || type === 'tbm_record') {
      setIsEditing(true);
      return;
    }
    if (type === 'cases') {
      const { extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
      const rawCases = extractAll('case', xmlContent);
      setEditCases(rawCases.map(c => {
        const { extract: e } = parseXmlString(c);
        const imgsStr = e('images', c) || '';
        let images: string[] = [];
        if (imgsStr) {
          try {
            const parsed = JSON.parse(imgsStr);
            if (Array.isArray(parsed)) {
              images = parsed.filter((u: unknown): u is string => typeof u === 'string' && u.length > 0);
            }
          } catch {}
        }
        return {
          title: e('title', c) || '',
          occurredAt: e('occurredAt', c) || '',
          accidentLocation: e('accidentLocation', c) || '',
          accidentDetails: e('accidentDetails', c) || '',
          accidentCause: e('accidentCause', c) || '',
          specificCause: e('specificCause', c) || '',
          preventiveMeasure: e('preventiveMeasure', c) || '',
          images,
        };
      }));
      setIsEditing(true);
      return;
    }
    if (type === 'laws') {
      const { extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
      const rawLaws = extractAll('law', xmlContent);
      setEditLaws(rawLaws.map(l => {
        const { extract: e } = parseXmlString(l);
        return {
          act: e('act', l) || '',
          article: e('article', l) || '',
          detail: e('detail', l) || '',
          paragraph: e('paragraph', l) || '',
          violation_risk: e('violation_risk', l) || '',
          download_url: e('download_url', l) || '',
        };
      }));
      setIsEditing(true);
      return;
    }
    const { extract, extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
    // 위험성평가 첨부 이미지 파일 ID 배열 — <attachment_file_ids><id>...</id></attachment_file_ids>
    const attIds = getRiskAttachmentFileIds(xmlContent);
    setEditAttachmentFileIds(attIds);
    const itemsBlock = extract('items', xmlContent) || xmlContent;
    const rawItems = extractAll('item', itemsBlock);
    setEditItems(rawItems.map((s, i) => {
      const { extract: e } = parseXmlString(s);
      return {
        id: (i + 1).toString(),
        prob_freq: e('prob_freq', s) || '',
        severity: e('severity', s) || '',
        hazard_primary_category: e('hazard_primary_category', s) || '',
        risk_category: e('risk_category', s) || '',
        accident_classification: e('accident_classification', s) || '',
        hazard: e('hazard', s) || e('hazard_analysis', s) || '',
        risk_level: e('risk_level', s) || e('risk_level_verbal', s) || '',
        reduction_measures_detail: parseBullets(e('reduction_measures_detail', s)),
        post_improvement_prob_freq: e('post_improvement_prob_freq', s) || '',
        post_improvement_severity: e('post_improvement_severity', s) || '',
        post_improvement_risk_level:
          e('post_improvement_risk_level', s) ||
          e('post_improvement_risk_level_verbal', s) ||
          '',
        basis: e('basis', s) || '',
        legal_basis_details: e('legal_basis_details', s) || '',
        check_result: e('check_result', s) || '',
        statistical_basis: e('statistical_basis', s) || '',
        recommended_prob_freq: e('recommended_prob_freq', s) || '',
        recommended_severity: e('recommended_severity', s) || '',
        recommended_risk_level: e('recommended_risk_level', s) || '',
      };
    }));
    const eq = extract('equipment', xmlContent) || '';
    const mat = extract('material', xmlContent) || '';
    setEditEquipment(parseRiskAssessmentListValue(eq));
    setEditMaterial(parseRiskAssessmentListValue(mat));
    setEqInput('');
    setMatInput('');
    setIsEditing(true);
  };

  const handleDragStart = (idx: number) => {
    dragRef.current = { fromIdx: idx, toIdx: idx };
  };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
    if (dragRef.current) dragRef.current.toIdx = idx;
  };
  const handleDragEnd = () => {
    if (dragRef.current && dragRef.current.fromIdx !== dragRef.current.toIdx) {
      const { fromIdx, toIdx } = dragRef.current;
      setEditItems(prev => {
        const a = [...prev];
        const [moved] = a.splice(fromIdx, 1);
        a.splice(toIdx, 0, moved);
        return a.map((item, i) => ({ ...item, id: (i + 1).toString() }));
      });
    }
    dragRef.current = null;
    setDragOverIdx(null);
  };

  const deleteItem = (idx: number) => {
    setEditItems(prev => prev.filter((_, i) => i !== idx).map((item, i) => ({ ...item, id: (i + 1).toString() })));
  };

  const addItem = () => {
    setEditItems(prev => [...prev, {
      id: (prev.length + 1).toString(), prob_freq: '', severity: '',
      hazard_primary_category: '', risk_category: '', accident_classification: '', hazard: '',
      risk_level: '', reduction_measures_detail: [''], basis: '', legal_basis_details: '', check_result: '',
      post_improvement_prob_freq: '', post_improvement_severity: '',
      post_improvement_risk_level: '',
      statistical_basis: '', recommended_prob_freq: '', recommended_severity: '',
      recommended_risk_level: '',
    }]);
  };

  const updateEditItem = (idx: number, field: string, value: string) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      if (field === 'prob_freq' || field === 'severity') {
        const probability = Number(next.prob_freq);
        const severity = Number(next.severity);
        next.risk_level = Number.isFinite(probability) && Number.isFinite(severity) && probability > 0 && severity > 0
          ? String(probability * severity)
          : '';
      }
      if (field === 'post_improvement_prob_freq' || field === 'post_improvement_severity') {
        const probability = Number(next.post_improvement_prob_freq);
        const severity = Number(next.post_improvement_severity);
        next.post_improvement_risk_level =
          next.post_improvement_prob_freq &&
          next.post_improvement_severity &&
          Number.isFinite(probability) &&
          Number.isFinite(severity) &&
          probability > 0 &&
          severity > 0
            ? String(probability * severity)
            : '';
      }
      if (field === 'basis' && value !== item.basis) {
        next.legal_basis_details = '';
      }
      return next;
    }));
  };

  const handleRiskFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!onSave) return;
    const formData = new FormData(e.currentTarget);

    let newXml = '';
    for (const item of editItems) {
      newXml += `  <item>\n`;
      newXml += `    <id>${item.id}</id>\n`;
      newXml += `    <accident_classification>${item.accident_classification}</accident_classification>\n`;
      if (riskMethodType === '빈도강도법') {
        newXml += `    <hazard_primary_category>${item.hazard_primary_category}</hazard_primary_category>\n`;
        newXml += `    <risk_category>${item.risk_category}</risk_category>\n`;
        newXml += `    <hazard>${item.hazard}</hazard>\n`;
        newXml += `    <prob_freq>${item.prob_freq}</prob_freq>\n`;
        newXml += `    <severity>${item.severity}</severity>\n`;
        newXml += `    <risk_level>${item.risk_level}</risk_level>\n`;
        newXml += `    <score_source>USER_CONFIRMED</score_source>\n`;
        if (item.recommended_prob_freq) newXml += `    <recommended_prob_freq>${item.recommended_prob_freq}</recommended_prob_freq>\n`;
        if (item.recommended_severity) newXml += `    <recommended_severity>${item.recommended_severity}</recommended_severity>\n`;
        if (item.recommended_risk_level) newXml += `    <recommended_risk_level>${item.recommended_risk_level}</recommended_risk_level>\n`;
        newXml += `    <reduction_measures_detail>${serializeBullets(item.reduction_measures_detail)}</reduction_measures_detail>\n`;
        newXml += `    <post_improvement_prob_freq>${item.post_improvement_prob_freq}</post_improvement_prob_freq>\n`;
        newXml += `    <post_improvement_severity>${item.post_improvement_severity}</post_improvement_severity>\n`;
        newXml += `    <post_improvement_risk_level>${item.post_improvement_risk_level}</post_improvement_risk_level>\n`;
        newXml += `    <basis>${item.basis}</basis>\n`;
      } else if (riskMethodType === '3단계 판단법') {
        newXml += `    <hazard_analysis>${item.hazard}</hazard_analysis>\n`;
        newXml += `    <risk_level_verbal>${item.risk_level}</risk_level_verbal>\n`;
        newXml += `    <reduction_measures_detail>${serializeBullets(item.reduction_measures_detail)}</reduction_measures_detail>\n`;
        newXml += `    <post_improvement_risk_level_verbal>${item.post_improvement_risk_level}</post_improvement_risk_level_verbal>\n`;
        newXml += `    <basis>${item.basis}</basis>\n`;
      } else {
        newXml += `    <hazard_analysis>${item.hazard}</hazard_analysis>\n`;
        newXml += `    <risk_level_verbal>${item.risk_level}</risk_level_verbal>\n`;
        newXml += `    <check_result>${item.check_result}</check_result>\n`;
        newXml += `    <reduction_measures_detail>${serializeBullets(item.reduction_measures_detail)}</reduction_measures_detail>\n`;
        newXml += `    <post_improvement_risk_level_verbal>${item.post_improvement_risk_level}</post_improvement_risk_level_verbal>\n`;
        newXml += `    <basis>${item.basis}</basis>\n`;
      }
      if (item.legal_basis_details) {
        newXml += `    <legal_basis_details>${item.legal_basis_details}</legal_basis_details>\n`;
      }
      if (item.statistical_basis) newXml += `    <statistical_basis>${item.statistical_basis}</statistical_basis>\n`;
      newXml += `  </item>\n`;
    }

    const { extract } = parseXmlString(`<root>${xmlContent}</root>`);
    const industryName = formData.get('industry_name') !== null ? formData.get('industry_name') as string : extract('industry_name', xmlContent);
    const processName = formData.get('process_name') !== null ? formData.get('process_name') as string : extract('process_name', xmlContent);
    const processDesc = formData.get('process_description') !== null ? formData.get('process_description') as string : extract('process_description', xmlContent);
    const equipment = normalizeRiskAssessmentListValue(editEquipment.join(', '));
    const material = normalizeRiskAssessmentListValue(editMaterial.join(', '));

    let fullXml = '';
    const methodValue = riskMethodType === '빈도강도법' ? `빈도강도법 ${riskMethod}` : riskMethodType;
    fullXml += `<method>${methodValue}</method>\n`;
    if (industryName) fullXml += `<industry_name>${industryName}</industry_name>\n`;
    if (processName) fullXml += `<process_name>${processName}</process_name>\n`;
    if (processDesc) fullXml += `<process_description>${processDesc}</process_description>\n`;
    if (equipment) fullXml += `<equipment>${equipment}</equipment>\n`;
    if (material) fullXml += `<material>${material}</material>\n`;

    const attachmentFileIds = normalizeAttachmentFileIds(editAttachmentFileIds);
    if (attachmentFileIds.length > 0) {
      fullXml += `<attachment_file_ids>\n`;
      for (const id of attachmentFileIds) fullXml += `  <id>${id}</id>\n`;
      fullXml += `</attachment_file_ids>\n`;
    }

    fullXml += newXml;
    onSave(fullXml, editTitle.trim() || title);
    setIsEditing(false);
  };

  // cases/laws 개별 행 필드 업데이트
  const updateEditCase = (idx: number, field: string, value: string) => {
    setEditCases(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };
  const updateEditLaw = (idx: number, field: string, value: string) => {
    setEditLaws(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const handleCasesFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!onSave) return;
    let xml = '';
    for (const c of editCases) {
      xml += '<case>\n';
      xml += `  <title>${c.title}</title>\n`;
      xml += `  <occurredAt>${c.occurredAt}</occurredAt>\n`;
      xml += `  <accidentLocation>${c.accidentLocation}</accidentLocation>\n`;
      xml += `  <accidentDetails>${c.accidentDetails}</accidentDetails>\n`;
      xml += `  <accidentCause>${c.accidentCause}</accidentCause>\n`;
      xml += `  <specificCause>${c.specificCause}</specificCause>\n`;
      xml += `  <preventiveMeasure>${c.preventiveMeasure}</preventiveMeasure>\n`;
      if (c.images && c.images.length > 0) {
        xml += `  <images>${JSON.stringify(c.images)}</images>\n`;
      }
      xml += '</case>\n';
    }
    onSave(xml, editTitle.trim() || title);
    setIsEditing(false);
  };

  // ── 사진 추가/삭제 헬퍼 ───────────────────────────────────
  const removeCaseImage = (caseIdx: number, imgIdx: number) => {
    setEditCases(prev => prev.map((c, i) => i === caseIdx
      ? { ...c, images: c.images.filter((_, j) => j !== imgIdx) }
      : c));
  };
  const addCaseImagesFromFileList = async (caseIdx: number, list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const newIds: string[] = [];
    for (const f of files) {
      const id = await uploadToFiles(f);
      if (id) newIds.push(`file:${id}`);
    }
    if (newIds.length > 0) {
      setEditCases(prev => prev.map((c, i) => i === caseIdx
        ? { ...c, images: [...c.images, ...newIds] }
        : c));
    }
  };

  const removeRiskAttachment = (idx: number) => {
    setEditAttachmentFileIds(prev => prev.filter((_, i) => i !== idx));
  };
  const addRiskAttachmentsFromFileList = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const newIds: string[] = [];
    for (const f of files) {
      const id = await uploadToFiles(f);
      if (id) newIds.push(id);
    }
    if (newIds.length > 0) {
      setEditAttachmentFileIds(prev => [...prev, ...newIds]);
    }
  };

  const handleLawsFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!onSave) return;
    let xml = '';
    for (const l of editLaws) {
      xml += '<law>\n';
      if (l.act) xml += `  <act>${l.act}</act>\n`;
      if (l.article) xml += `  <article>${l.article}</article>\n`;
      if (l.detail) xml += `  <detail>${l.detail}</detail>\n`;
      if (l.paragraph) xml += `  <paragraph>${l.paragraph}</paragraph>\n`;
      if (l.violation_risk) xml += `  <violation_risk>${l.violation_risk}</violation_risk>\n`;
      if (l.download_url) xml += `  <download_url>${l.download_url}</download_url>\n`;
      xml += '</law>\n';
    }
    onSave(xml, editTitle.trim() || title);
    setIsEditing(false);
  };

  const renderLaws = () => {
    const { extract, extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
    const laws = extractAll('law', xmlContent);

    if (!laws || laws.length === 0) {
      return (
        <div className="flex flex-col gap-4 w-full animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-2xl p-6 shadow-sm flex flex-col gap-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 flex flex-col gap-2">
                  <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-md shrink-0" />
              </div>
              <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 flex flex-col gap-2">
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-4/5 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="h-2.5 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    const riskColors: Record<string, { bg: string; text: string; border: string }> = {
      '높음': { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-900/50' },
      '중간': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-900/50' },
      '낮음': { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-900/50' },
    };

    const inputCls = "w-full text-sm border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus:outline-none focus:border-seexr-primary bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold";

    return (
      <form id="laws-form" onSubmit={handleLawsFormSubmit} className="flex flex-col gap-4 w-full">
        <div className="flex items-center gap-2 mb-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-seexr-primary"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
          <span className="text-[15px] font-black text-gray-800 dark:text-gray-200">관련 법령 {isEditing ? editLaws.length : laws.length}건</span>
        </div>
        {(isEditing ? editLaws.map((l, idx) => ({ idx, l, srcStr: '' })) : laws.map((lawStr, idx) => ({ idx, lawStr }))).map((entry: any) => {
          const idx = entry.idx;
          const act = isEditing ? entry.l.act : extract('act', entry.lawStr);
          const article = isEditing ? entry.l.article : extract('article', entry.lawStr);
          const detail = isEditing ? entry.l.detail : extract('detail', entry.lawStr);
          const paragraph = isEditing ? entry.l.paragraph : extract('paragraph', entry.lawStr);
          const violationRisk = isEditing ? entry.l.violation_risk : extract('violation_risk', entry.lawStr);
          const downloadUrl = isEditing ? entry.l.download_url : extract('download_url', entry.lawStr);
          const colors = riskColors[violationRisk] || riskColors['중간'];

          return (
            <div key={idx} className="bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-2xl p-6 shadow-sm flex flex-col">
              <div className="flex justify-between items-start mb-3 gap-3">
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  {isEditing ? (
                    <>
                      <input type="text" value={act} onChange={e => updateEditLaw(idx, 'act', e.target.value)} className={inputCls} placeholder="법령명" />
                      <input type="text" value={article} onChange={e => updateEditLaw(idx, 'article', e.target.value)} className={inputCls} placeholder="조항" />
                    </>
                  ) : (
                    <>
                      <div className="text-[12px] font-black text-seexr-primary uppercase tracking-wider mb-1">{act}</div>
                      <h3 className="font-black text-[17px] text-gray-900 dark:text-gray-100 leading-tight">{article}</h3>
                    </>
                  )}
                </div>
                {isEditing ? (
                  <select value={violationRisk} onChange={e => updateEditLaw(idx, 'violation_risk', e.target.value)} className={`text-[11px] font-black px-2 py-1 rounded-md shrink-0 border ${colors.bg} ${colors.text} ${colors.border} focus:outline-none`}>
                    <option value="">위반위험</option>
                    <option value="낮음">낮음</option>
                    <option value="중간">중간</option>
                    <option value="높음">높음</option>
                  </select>
                ) : (violationRisk && (
                  <span className={`text-[11px] font-black px-2.5 py-1 rounded-md shrink-0 ml-4 border ${colors.bg} ${colors.text} ${colors.border}`}>위반위험 {violationRisk}</span>
                ))}
              </div>

              {isEditing ? (
                <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 mb-4 border border-gray-100 dark:border-[#2a2a2e]">
                  <textarea rows={2} value={detail} onChange={e => updateEditLaw(idx, 'detail', e.target.value)} className={`${inputCls} resize-none`} placeholder="상세 설명" />
                </div>
              ) : (detail && (
                <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 mb-4 border border-gray-100 dark:border-[#2a2a2e]">
                  <p className="text-[14px] text-gray-700 dark:text-gray-300 leading-relaxed font-medium">{detail}</p>
                </div>
              ))}

              <div className="mb-4">
                <span className="text-[12px] font-black text-gray-400 dark:text-gray-500 block mb-2 uppercase tracking-wide">조문 내용</span>
                {isEditing ? (
                  <textarea rows={4} value={paragraph} onChange={e => updateEditLaw(idx, 'paragraph', e.target.value)} className={`${inputCls} resize-none`} placeholder="조문 내용" />
                ) : (paragraph && (
                  <div className="text-[13px] font-medium text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap bg-gray-50/50 dark:bg-[#1a1a1c] rounded-lg p-3 border border-gray-100 dark:border-[#252528]">{paragraph}</div>
                ))}
              </div>

              {isEditing ? (
                <input type="text" value={downloadUrl} onChange={e => updateEditLaw(idx, 'download_url', e.target.value)} className={inputCls} placeholder="PDF 다운로드 URL (선택)" />
              ) : (downloadUrl && (
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-seexr-primary hover:underline mt-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  별표/서식 PDF 다운로드
                </a>
              ))}
            </div>
          );
        })}
      </form>
    );
  };

  const renderCases = () => {
    const { extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
    const cases = extractAll('case', xmlContent);

    if (!cases || cases.length === 0) {
      return (
        <div className="flex flex-col gap-4 w-full animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-2xl p-6 shadow-sm flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="h-5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <div className="h-2.5 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="h-2.5 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 flex flex-col gap-2">
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-4/5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-5/6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 flex flex-col gap-2">
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    const inputCls = "w-full text-sm border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus:outline-none focus:border-seexr-primary bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold";

    const rows = isEditing
      ? editCases.map((c, idx) => ({ idx, c, caseStr: '' }))
      : cases.map((caseStr, idx) => ({ idx, caseStr, c: null as any }));

    return (
      <form id="cases-form" onSubmit={handleCasesFormSubmit} className="flex flex-col gap-4 w-full">
        {rows.map((entry) => {
          const idx = entry.idx;
          const caseStr = entry.caseStr;
          const { extract } = parseXmlString(caseStr || '');
          const title = isEditing ? entry.c.title : extract('title', caseStr);
          const occurredAt = isEditing ? entry.c.occurredAt : extract('occurredAt', caseStr);
          const accidentLocation = isEditing ? entry.c.accidentLocation : extract('accidentLocation', caseStr);
          const accidentDetails = isEditing ? entry.c.accidentDetails : extract('accidentDetails', caseStr);
          const accidentCause = isEditing ? entry.c.accidentCause : extract('accidentCause', caseStr);
          const specificCause = isEditing ? entry.c.specificCause : extract('specificCause', caseStr);
          const preventiveMeasure = isEditing ? entry.c.preventiveMeasure : extract('preventiveMeasure', caseStr);
          // 편집 모드에선 editCases.images(파싱 완료된 배열)를 바로 사용,
          // 읽기 모드에선 XML에서 images JSON 문자열을 파싱
          let images: string[] = [];
          if (isEditing) {
            images = Array.isArray(entry.c.images) ? entry.c.images : [];
          } else {
            const imagesStr = extract('images', caseStr);
            if (imagesStr) {
              try {
                const parsed = JSON.parse(imagesStr);
                if (Array.isArray(parsed)) {
                  images = parsed.filter((url: string) => url && url.length > 5 && !url.includes('...'));
                }
              } catch (e) {}
            }
          }

          return (
            <div key={idx} className="bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-2xl p-6 shadow-sm flex flex-col">
              <div className="flex justify-between items-start mb-4 gap-3">
                {isEditing ? (
                  <input type="text" value={title} onChange={e => updateEditCase(idx, 'title', e.target.value)} className={`${inputCls} flex-1`} placeholder="사례 제목" />
                ) : (
                  <h3 className="font-black text-[17px] text-gray-900 dark:text-gray-100 leading-tight flex-1">{title}</h3>
                )}
                {isEditing ? (
                  <input type="date" value={(occurredAt || '').split('T')[0]} onChange={e => updateEditCase(idx, 'occurredAt', e.target.value)} className="text-[12px] font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#1f1f22] border border-gray-200 dark:border-[#2a2a2e] px-2 py-1 rounded-md shrink-0 focus:outline-none focus:border-seexr-primary" />
                ) : (
                  <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#1f1f22] border border-gray-100 dark:border-[#2a2a2e] px-2.5 py-1 rounded-md shrink-0 ml-4">
                    {(occurredAt || '').split('T')[0]}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mb-4">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-seexr-primary shrink-0" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {isEditing ? (
                  <input type="text" value={accidentLocation} onChange={e => updateEditCase(idx, 'accidentLocation', e.target.value)} className={`${inputCls} text-seexr-primary`} placeholder="사고 발생 위치" />
                ) : (accidentLocation && (
                  <p className="text-[13px] font-bold text-seexr-primary">{accidentLocation}</p>
                ))}
              </div>

              <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 mb-5 border border-gray-100 dark:border-[#2a2a2e]">
                {isEditing ? (
                  <textarea rows={3} value={accidentDetails} onChange={e => updateEditCase(idx, 'accidentDetails', e.target.value)} className={`${inputCls} resize-none`} placeholder="사고 상세" />
                ) : (
                  <p className="text-[14px] text-gray-700 dark:text-gray-300 leading-relaxed font-medium whitespace-pre-wrap">{accidentDetails}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                  <div>
                    <span className="text-[12px] font-black text-gray-400 dark:text-gray-500 block mb-2 uppercase tracking-wide">Accident Cause</span>
                    {isEditing ? (
                      <div className="flex flex-col gap-1.5">
                        <input type="text" value={accidentCause} onChange={e => updateEditCase(idx, 'accidentCause', e.target.value)} className={inputCls} placeholder="사고 원인" />
                        <textarea rows={2} value={specificCause} onChange={e => updateEditCase(idx, 'specificCause', e.target.value)} className={`${inputCls} resize-none`} placeholder="구체적 원인" />
                      </div>
                    ) : (
                      <>
                        <p className="text-[14px] font-bold text-gray-800 dark:text-gray-200 mb-1">{accidentCause}</p>
                        <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400 leading-relaxed">{specificCause}</p>
                      </>
                    )}
                  </div>
                  <div>
                    <span className="text-[12px] font-black text-gray-400 dark:text-gray-500 block mb-2 uppercase tracking-wide">Preventive Measure</span>
                    {isEditing ? (
                      <textarea rows={3} value={preventiveMeasure} onChange={e => updateEditCase(idx, 'preventiveMeasure', e.target.value)} className={`${inputCls} resize-none`} placeholder="예방 대책" />
                    ) : (
                      <p className="text-[13px] font-medium text-gray-700 dark:text-gray-300 leading-relaxed">{preventiveMeasure}</p>
                    )}
                  </div>
              </div>

              {/* 사진 섹션 — 편집 모드에서도 표시, 각 썸네일에 삭제(X) + 추가 버튼 */}
              {(isEditing || images.length > 0) && (
                <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-2 pt-2 mt-auto border-t border-gray-100 dark:border-[#2a2a2e]">
                  {images.map((img, i) => {
                    // ArtifactImg: 파일 업로드된 이미지는 "file:{uuid}" 프리픽스
                    const isFile = img.startsWith('file:');
                    const fileId = isFile ? img.slice(5) : '';
                    const artifactImgs: ArtifactImg[] = images.map(src => src.startsWith('file:')
                      ? { kind: 'file' as const, fileId: src.slice(5) }
                      : { kind: 'url' as const, url: src });
                    return (
                      <div
                        key={i}
                        className="h-32 w-48 shrink-0 rounded-xl overflow-hidden border border-gray-200 dark:border-[#2a2a2e] shadow-sm relative group"
                      >
                        <div
                          className="h-full w-full cursor-pointer"
                          onClick={() => !isEditing && setViewerState({ images: artifactImgs, index: i })}
                        >
                          {isFile ? (
                            <AuthImg fileId={fileId} className="h-full w-full object-cover" alt="attachment" />
                          ) : (
                            <img
                              src={img}
                              alt="accident case"
                              className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          )}
                        </div>
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => removeCaseImage(idx, i)}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center shadow-md transition-colors"
                            aria-label="사진 삭제"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {isEditing && (
                    <label className="h-32 w-32 shrink-0 rounded-xl border-2 border-dashed border-gray-300 dark:border-[#3a3a3e] flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:border-seexr-primary hover:bg-seexr-solid/5 transition-colors text-gray-400 hover:text-seexr-primary">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <span className="text-[11px] font-bold">사진 추가</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => { addCaseImagesFromFileList(idx, e.target.files); e.target.value = ''; }}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </form>
    );
  };

  const renderCostEstimate = () => {
    const { extract, extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
    const projectParams = extract('project_params', xmlContent);
    const matchedProject = extract('matched_project', xmlContent);
    const costCalculation = extract('cost_calculation', xmlContent);
    const materialsBlock = extract('materials', xmlContent);
    const materials = materialsBlock
      ? [...extractAll('material', materialsBlock), ...extractAll('item', materialsBlock)]
      : [];

    const text = (tag: string, source = xmlContent) => extract(tag, source);
    const formatNumber = (value: string) => {
      const n = Number(String(value || '').replace(/,/g, ''));
      return Number.isFinite(n) ? new Intl.NumberFormat('ko-KR').format(n) : value;
    };
    const formatWon = (value: string) => {
      const n = Number(String(value || '').replace(/,/g, ''));
      if (!Number.isFinite(n)) return value || '-';
      const eok = n / 100000000;
      return `${new Intl.NumberFormat('ko-KR').format(Math.round(n))}원 (${eok.toFixed(1)}억 원)`;
    };
    const formatPercent = (value: string) => {
      const n = Number(String(value || '').replace(/,/g, ''));
      if (!Number.isFinite(n)) return value || '-';
      return n <= 1 ? `${(n * 100).toFixed(1)}%` : `${n.toFixed(4)}x`;
    };
    const display = (value: string) => value || '-';
    const horizontalTable = (columns: Array<[string, string]>, minWidth: number) => (
      <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
        <table className="w-full text-left" style={{ minWidth }}>
          <thead className="bg-gray-50/80 text-[11px] uppercase tracking-widest text-gray-400 dark:bg-[#1b1b1e]">
            <tr>
              {columns.map(([label]) => (
                <th key={label} className="px-3 py-2.5 font-black">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100 text-[13px] font-bold text-gray-800 dark:border-[#2a2a2e] dark:text-gray-100">
              {columns.map(([label, value]) => (
                <td key={label} className="px-3 py-2.5 align-top">
                  {display(value)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );

    const adjustedCost = text('adjusted_cost', costCalculation);
    const baseCost = text('base_cost', costCalculation);

    return (
      <div className="flex flex-col gap-7">
        <section className="px-1 pb-5 border-b border-gray-200 dark:border-[#2a2a2e]">
          <div className="text-[12px] font-black uppercase tracking-widest text-seexr-primary">Estimated Cost</div>
          <div className="mt-2 text-[32px] font-black tracking-normal text-gray-950 dark:text-gray-50">
            {formatWon(adjustedCost)}
          </div>
          <div className="mt-2 text-[13px] font-semibold text-gray-500 dark:text-gray-400">
            기준 공사비 {formatWon(baseCost)} · CCI 보정계수 {formatPercent(text('cci_adjustment_factor', costCalculation))}
          </div>
        </section>

        <section>
          <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">프로젝트 조건</h3>
          {horizontalTable([
            ['공사 종류', text('building_type', projectParams)],
            ['구조', text('structure', projectParams)],
            ['지하층', text('underground', projectParams)],
            ['지상층', text('above_ground', projectParams)],
            ['연면적', formatNumber(text('floor_area', projectParams))],
            ['지역', text('region', projectParams)],
            ['공사기간', text('construction_period', projectParams)],
          ], 860)}
        </section>

        <section>
          <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">유사 프로젝트</h3>
          {horizontalTable([
            ['프로젝트명', text('name', matchedProject)],
            ['유사도', text('similarity_score', matchedProject)],
            ['공고월', text('announcement_date', matchedProject)],
            ['구조', text('structure', matchedProject)],
            ['지역', text('region', matchedProject)],
          ], 680)}
        </section>

        <section>
          <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">공사비 보정</h3>
          {horizontalTable([
            ['기준 CCI 월', text('cci_base_month', costCalculation)],
            ['기준 CCI', text('cci_base_value', costCalculation)],
            ['최신 CCI 월', text('cci_latest_month', costCalculation)],
            ['최신 CCI', text('cci_latest_value', costCalculation)],
            ['구조 보정', text('structure_adjusted', costCalculation)],
            ['보정계수', text('cci_adjustment_factor', costCalculation)],
          ], 760)}
        </section>

        {materials.length > 0 && (
          <section>
            <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">마감/자재</h3>
            <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
              <table className="w-full min-w-[560px] text-left">
                <thead className="bg-gray-50/80 dark:bg-[#1b1b1e] text-[11px] uppercase tracking-widest text-gray-400">
                  <tr>
                    <th className="px-3 py-2.5 font-black">위치</th>
                    <th className="px-3 py-2.5 font-black">자재</th>
                    <th className="px-3 py-2.5 font-black">단위</th>
                    <th className="px-3 py-2.5 font-black">단가</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((material, idx) => (
                    <tr key={idx} className="border-t border-gray-100 dark:border-[#2a2a2e] text-[13px] font-medium text-gray-700 dark:text-gray-300">
                      <td className="px-3 py-2.5">{text('location', material) || '-'}</td>
                      <td className="px-3 py-2.5">{text('material_name', material) || text('name', material) || '-'}</td>
                      <td className="px-3 py-2.5">{text('unit', material) || '-'}</td>
                      <td className="px-3 py-2.5">{formatNumber(text('unit_price', material)) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderCostEstimateMaterialChange = () => {
    const { extract, extractAll } = parseXmlString(`<root>${xmlContent}</root>`);
    const changesBlock = extract('changes', xmlContent);
    const changes = extractAll('change', changesBlock || xmlContent);
    const summary = extract('summary', xmlContent);
    const text = (tag: string, source = xmlContent) => extract(tag, source);
    const numberValue = (value: string) => Number(String(value || '').replace(/,/g, ''));
    const formatNumber = (value: string) => {
      const n = numberValue(value);
      return Number.isFinite(n) ? new Intl.NumberFormat('ko-KR').format(Math.round(n)) : value || '-';
    };
    const formatWon = (value: string) => {
      const n = numberValue(value);
      return Number.isFinite(n) ? `${new Intl.NumberFormat('ko-KR').format(Math.round(n))}원` : value || '-';
    };
    const formatDifference = (value: string) => {
      const n = numberValue(value);
      if (!Number.isFinite(n)) return value || '-';
      const sign = n > 0 ? '+' : '';
      return `${sign}${new Intl.NumberFormat('ko-KR').format(Math.round(n))}원`;
    };
    const differenceClass = (value: string) => {
      const n = numberValue(value);
      if (!Number.isFinite(n) || n === 0) return 'text-gray-700 dark:text-gray-200';
      return n > 0 ? 'text-red-600 dark:text-red-400' : 'text-seexr-600 dark:text-seexr-400';
    };
    const field = (label: string, value: string, className = 'text-gray-900 dark:text-gray-100') => (
      <div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2.5 dark:border-[#2a2a2e] dark:bg-[#1b1b1e]">
        <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</div>
        <div className={`mt-1 truncate text-[13px] font-black ${className}`}>{value || '-'}</div>
      </div>
    );

    const totalDifference = text('total_difference', summary);

    return (
      <div className="flex flex-col gap-4">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
          <div className="text-[12px] font-black uppercase tracking-widest text-gray-500">Material Change Preview</div>
          <div className={`mt-2 text-[26px] font-black tracking-normal ${differenceClass(totalDifference)}`}>
            {formatDifference(totalDifference)}
          </div>
          <div className="mt-2 text-[12px] font-semibold text-gray-500 dark:text-gray-400">
            변경 전 영향 금액 {formatWon(text('before_total', summary))} · 변경 후 영향 금액 {formatWon(text('after_total', summary))}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
          <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">총 공사비 변화</h3>
          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {field('현재 총 공사비', formatWon(text('current_project_cost', summary)))}
            {field('변경 후 예상 공사비', formatWon(text('new_project_total', summary)))}
            {field('총 차액', formatDifference(totalDifference), differenceClass(totalDifference))}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
          <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100">자재 변경안</h3>
          <div className="mt-4 overflow-x-auto rounded-lg border border-gray-100 dark:border-[#2a2a2e]">
            <table className="w-full min-w-[760px] text-left">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-widest text-gray-400 dark:bg-[#1f1f22]">
                <tr>
                  <th className="px-3 py-2">위치</th>
                  <th className="px-3 py-2">기존 자재</th>
                  <th className="px-3 py-2">변경 자재</th>
                  <th className="px-3 py-2">수량</th>
                  <th className="px-3 py-2">기존 단가</th>
                  <th className="px-3 py-2">변경 단가</th>
                  <th className="px-3 py-2">차액</th>
                </tr>
              </thead>
              <tbody>
                {changes.length > 0 ? changes.map((change, idx) => {
                  const diff = text('difference', change);
                  return (
                    <tr key={idx} className="border-t border-gray-100 text-[13px] font-medium text-gray-700 dark:border-[#2a2a2e] dark:text-gray-300">
                      <td className="px-3 py-2">{text('location', change) || '-'}</td>
                      <td className="px-3 py-2">{text('original_material', change) || '-'}</td>
                      <td className="px-3 py-2 font-bold text-gray-900 dark:text-gray-100">{text('new_material', change) || '-'}</td>
                      <td className="px-3 py-2">{formatNumber(text('quantity', change))} {text('unit', change)}</td>
                      <td className="px-3 py-2">{formatWon(text('original_unit_price', change))}</td>
                      <td className="px-3 py-2">{formatWon(text('new_unit_price', change))}</td>
                      <td className={`px-3 py-2 font-black ${differenceClass(diff)}`}>{formatDifference(diff)}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-[13px] font-bold text-gray-400">표시할 변경안이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  };


  return (
    <div className="flex flex-col h-full bg-[#FAFAFA] dark:bg-[#111112]">
      {/* Header */}
      <div className="h-14 flex items-center gap-3 px-6 border-b border-gray-200 dark:border-[#2a2a2e] shrink-0 bg-white dark:bg-[#161618]">
         <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="w-6 h-6 shrink-0 rounded-md bg-seexr-solid/10 flex items-center justify-center text-seexr-primary">
               <svg fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"></path></svg>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                aria-label="아티팩트 제목"
                className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[15px] font-black text-gray-900 outline-none transition-colors focus:border-seexr-primary dark:border-[#343439] dark:bg-[#202023] dark:text-gray-100"
              />
            ) : sessionArtifacts.length > 1 && onSelectArtifact ? (
              <div ref={artifactSwitcherRef} className="relative min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => setIsArtifactSwitcherOpen(open => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={isArtifactSwitcherOpen}
                  title="이 세션의 다른 아티팩트 보기"
                  className="group flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-gray-100 dark:hover:bg-[#2a2a2e]"
                >
                  <span className="min-w-0 truncate font-black text-[15px] text-gray-900 dark:text-gray-100">{title}</span>
                  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400 transition-colors group-hover:text-seexr-primary">
                    <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>
                  </svg>
                </button>
                {isArtifactSwitcherOpen && (
                  <div role="listbox" aria-label="이 세션의 아티팩트" className="absolute left-0 top-full z-50 mt-1.5 w-[min(300px,calc(100vw-3rem))] overflow-hidden rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-[#343439] dark:bg-[#202023]">
                    <div className="px-2.5 pb-1.5 pt-1 text-[10px] font-black uppercase tracking-wider text-gray-400">이 채팅의 아티팩트</div>
                    <div className="max-h-72 overflow-y-auto">
                      {sessionArtifacts.map((artifact) => {
                        const isActive = artifact.key === activeArtifactKey;
                        return (
                          <button
                            key={artifact.key}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            onClick={() => {
                              setIsArtifactSwitcherOpen(false);
                              if (!isActive) onSelectArtifact(artifact.key);
                            }}
                            className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${isActive ? 'bg-seexr-solid/10 text-seexr-primary' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-[#2a2a2e]'}`}
                          >
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${isActive ? 'bg-seexr-solid/15' : 'bg-gray-100 text-gray-400 dark:bg-[#2a2a2e]'}`}>
                              <svg fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[13px] font-bold">{artifact.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <h2 className="min-w-0 flex-1 truncate font-black text-[15px] text-gray-900 dark:text-gray-100" title={title}>{title}</h2>
            )}
         </div>
         <div className="flex shrink-0 items-center gap-1.5">
           {type === 'risk_assessment' && !isEditing && canViewRiskArtifact && (
             <span ref={setRiskHeaderActionsTarget} className="contents" />
           )}
           {type === 'risk_assessment' && !isEditing && canViewRiskArtifact && (
             <button
               ref={downloadBtnRef}
               type="button"
               onClick={(e) => {
                 e.stopPropagation();
                 if (downloadAnchorRect) {
                   setDownloadAnchorRect(null);
                 } else {
                   const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                   setDownloadAnchorRect(r);
                 }
               }}
               title="다운로드"
               className={`p-1.5 rounded-md transition-colors ${downloadAnchorRect ? 'bg-gray-100 dark:bg-[#2a2a2e] text-gray-700 dark:text-gray-200' : 'hover:bg-gray-100 dark:hover:bg-[#2a2a2e] text-gray-500'}`}
             >
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
             </button>
           )}
           {canEditArtifact && canViewRiskArtifact && (
           <button
             type="button"
             onClick={() => {
                 if (isEditing) {
                   const formId = type === 'risk_assessment' ? 'risk-form'
                     : type === 'tbm_scenario' ? 'tbm-scenario-form'
                     : type === 'tbm_record' ? 'tbm-record-form'
                     : type === 'cases' ? 'cases-form'
                     : type === 'laws' || type === 'legal_search' ? 'laws-form'
                     : type === 'accident_search' ? 'cases-form'
                     : null;
                   if (formId) {
                     const form = document.getElementById(formId) as HTMLFormElement;
                     if (form) form.requestSubmit();
                   }
                } else {
                   startEditing();
                }
             }}
             className={`px-3 py-1.5 mr-1 rounded-md text-[13px] font-black transition-all shadow-sm flex items-center gap-1.5 ${isEditing ? 'bg-gray-900 border border-transparent dark:bg-white text-white dark:text-gray-900 hover:scale-105' : 'bg-seexr-solid/10 text-seexr-primary hover:bg-seexr-solid/20'}`}
           >
             {isEditing ? (
               <>
                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                 저장
               </>
             ) : (
               <>
                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                 편집
               </>
             )}
           </button>
           )}
           {onToggleExpand && (
             <button onClick={onToggleExpand} className="max-md:hidden p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-[#2a2a2e] text-gray-500 transition-colors">
               {isExpanded ? (
                 <Minimize2 size={20} />
               ) : (
                 <Maximize2 size={20} />
               )}
             </button>
           )}
           {showCloseButton && (
             <button aria-label="아티팩트 닫기" onClick={onClose} className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-[#2a2a2e] text-gray-500 transition-colors ${closeButtonClassName ?? ""}`}>
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
             </button>
           )}
         </div>
      </div>

      {/* Content */}
      <div className="relative min-h-0 flex-1">
        <div
          className={`custom-scrollbar h-full overflow-y-auto p-6 transition-[filter,opacity] duration-300 ${canViewRiskArtifact ? '' : 'pointer-events-none select-none'}`}
          aria-hidden={!canViewRiskArtifact}
          inert={!canViewRiskArtifact ? true : undefined}
        >
         <div className={`${artifactContentWidthClassName} mx-auto w-full pb-10 transition-all duration-300`}>
           {type === 'cases' && renderCases()}
           {type === 'risk_assessment' && (
             <RiskAssessmentReport
               xmlContent={xmlContent}
               isEditing={isEditing}
               riskMethodType={riskMethodType}
               riskMethod={riskMethod}
               setRiskMethod={setRiskMethod}
               showMethodPicker={showMethodPicker}
               setShowMethodPicker={setShowMethodPicker}
               editItems={editItems}
               setEditItems={setEditItems}
               editEquipment={editEquipment}
               setEditEquipment={setEditEquipment}
               editMaterial={editMaterial}
               setEditMaterial={setEditMaterial}
               eqInput={eqInput}
               setEqInput={setEqInput}
               matInput={matInput}
               setMatInput={setMatInput}
               editAttachmentFileIds={editAttachmentFileIds}
               dragOverIdx={dragOverIdx}
               parseXmlString={parseXmlString}
               getRiskAttachmentFileIds={getRiskAttachmentFileIds}
               handleRiskFormSubmit={handleRiskFormSubmit}
               removeRiskAttachment={removeRiskAttachment}
               addRiskAttachmentsFromFileList={addRiskAttachmentsFromFileList}
               setViewerState={setViewerState}
               handleDragStart={handleDragStart}
               handleDragOver={handleDragOver}
               handleDragEnd={handleDragEnd}
               updateEditItem={updateEditItem}
               deleteItem={deleteItem}
               addItem={addItem}
               bulletLines={bulletLines}
               AuthImg={AuthImg}
               sourceDocuments={sourceDocuments}
               projectSourceReferences={projectSourceReferences}
               SourceDocumentCard={ArtifactSourceDocumentCard}
               artifactId={resolvedRiskArtifactId}
               sessionId={sessionId}
               messageId={messageId}
               headerActionsTarget={riskHeaderActionsTarget}
             />
           )}
           {type === 'tbm_scenario' && (
             <TbmScenarioReport
               xmlContent={xmlContent}
               isEditing={isEditing}
               onSave={async (nextXml) => {
                 try {
                   await onSave?.(nextXml, editTitle.trim() || title);
                   setIsEditing(false);
                 } catch {
                   // The caller reports the persistence error. Keep the editor open
                   // so the user can retry without losing the edited scenario.
                 }
               }}
             />
           )}
           {type === 'tbm_record' && (
             <TbmRecordReport
               xmlContent={xmlContent}
               isEditing={isEditing}
               onSave={async (nextXml) => {
                 try {
                   await onSave?.(nextXml, editTitle.trim() || title);
                   setIsEditing(false);
                 } catch {
                   // Keep the record draft open when persistence fails.
                 }
               }}
             />
           )}
           {type === 'laws' && renderLaws()}
           {type === 'cost_estimate' && renderCostEstimate()}
           {type === 'cost_estimate_material_change' && renderCostEstimateMaterialChange()}
           {(type === 'risk_assessment' || isTbmArtifact) && canViewRiskArtifact && !isEditing && (
             <section className="mt-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
               <div className="flex items-start gap-3">
                 <input
                   id={`${type}-acknowledgement-toggle`}
                   type="checkbox"
                   checked
                   disabled={riskAcknowledgementSaving}
                   onChange={(event) => {
                     if (!event.target.checked) void updateRiskAcknowledgement(false);
                   }}
                   className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-seexr-solid disabled:cursor-wait"
                 />
                 <div className="flex min-w-0 flex-1 items-start gap-1.5">
                   <label htmlFor={`${type}-acknowledgement-toggle`} className="cursor-pointer text-[13px] font-black leading-5 text-gray-900 dark:text-gray-100">
                     {isTbmArtifact
                       ? 'AI TBM 자료를 현장 여건에 맞게 검토·보완하고 실행 결과를 직접 확인할 책임이 있음을 이해하였음.'
                       : 'AI 위험성평가의 활용 범위와 결과에 대한 최종 책임이 사업주에게 있음을 이해하였음.'}
                   </label>
                   <RiskAcknowledgementHelp />
                 </div>
               </div>
               <hr className="my-3 border-0 border-t border-gray-100 dark:border-[#2a2a2e]" />
               <p className="text-[11px] font-semibold leading-[1.65] text-gray-500 dark:text-gray-400">
                 {isTbmArtifact
                   ? 'TBM 시나리오와 기록지는 작업 전 대화를 지원하는 초안입니다. 실제 작업 조건, 참석자의 의견과 이해 여부, 조치 결과는 현장에서 직접 확인하고 기록해야 합니다.'
                   : '산업안전보건법에 따른 위험성평가의 실시 및 결과에 대한 최종 책임은 사업주에게 있으며, AI를 활용한 위험성평가는 위험요인 도출 및 개선 검토를 지원하는 하나의 수단으로 활용되어야 합니다.'}
               </p>
             </section>
           )}
           {type === 'risk_assessment' && canViewRiskArtifact && !isEditing && (
             <RiskAssessmentSurveyEditEntry survey={riskAssessmentSurvey} />
           )}
         </div>
        </div>

        {requiresRiskAcknowledgement && !canViewRiskArtifact && (
          <div className="absolute inset-0 z-30 flex items-center justify-center overflow-y-auto bg-slate-800/20 p-5 backdrop-blur-[9px] dark:bg-black/60">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="risk-acknowledgement-title"
              className="my-auto w-full max-w-[455px] overflow-hidden rounded-[16px] border border-gray-200/90 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.26)] dark:border-[#343439] dark:bg-[#18181b] dark:shadow-[0_24px_80px_rgba(0,0,0,0.58)]"
            >
              <header className="flex min-h-[58px] items-center gap-3 border-b border-gray-200 bg-white px-5 dark:border-[#2a2a2e] dark:bg-black">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-500 shadow-[0_0_18px_rgba(245,158,11,0.18)] dark:bg-amber-500/15 dark:text-amber-400">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                </div>
                <h3 id="risk-acknowledgement-title" className="min-w-0 flex-1 text-[14px] font-black tracking-tight text-slate-800 dark:text-gray-100">
                  {isTbmArtifact ? 'AI TBM 자료 활용 안내 및 책임 확인' : 'AI 위험성평가 활용 안내 및 책임 확인'}
                </h3>
              </header>

              <div className="px-5 py-5 sm:px-6">
                {riskAcknowledgementLoading ? (
                  <div className="flex min-h-56 flex-col items-center justify-center text-center">
                    <span className="h-6 w-6 animate-spin rounded-full border-2 border-seexr-primary/25 border-t-seexr-primary" />
                    <p className="mt-3 text-[12px] font-bold text-gray-500 dark:text-gray-400">열람 동의 상태를 확인하고 있습니다.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 text-[12px] font-medium leading-[1.8] text-slate-600 dark:text-gray-300">
                      <p>
                        {isTbmArtifact
                          ? '본 TBM 자료는 완료된 위험성평가를 작업 전 안전대화에 활용하도록 지원하는 초안입니다.'
                          : '본 AI 위험성평가 시스템은 산업안전보건법 및 관련 규정에 따른 위험성평가를 보조하는 도구입니다.'}
                      </p>

                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[11px] font-bold leading-[1.75] text-slate-600 dark:border-[#343439] dark:bg-[#202023] dark:text-gray-300">
                        {isTbmArtifact
                          ? '진행자는 작업 직전 현장 상태와 참석자 의견을 확인하고, 안전하지 않은 경우 작업을 중지한 뒤 필요한 조치를 완료해야 합니다.'
                          : '산업안전보건법에 따른 위험성평가의 실시 및 결과에 대한 최종 책임은 사업주에게 있으며, AI를 활용한 위험성평가는 위험요인 도출 및 개선 검토를 지원하는 하나의 수단으로 활용되어야 합니다.'}
                      </div>

                      <p>
                        본 시스템에서 제공되는 결과는 현장 여건, 작업 특성, 근로자 의견 등을 고려하여 사용자가 직접 검토·보완 후 적용하여야 합니다.
                      </p>

                      <p className="font-black text-slate-700 dark:text-gray-200">
                        {isTbmArtifact
                          ? '이에 TBM 진행과 실행 결과 확인 및 기록에 대한 책임이 사용자에게 있음을 확인합니다.'
                          : '이에 본 시스템을 활용함에 있어 위험성평가의 법적 책임이 사용자에게 있음을 확인합니다.'}
                      </p>
                    </div>

                    {riskAcknowledgementError && (
                      <div className="mt-4 flex items-start justify-between gap-3 rounded-lg bg-red-50 px-3 py-2.5 dark:bg-red-950/20">
                        <p role="alert" className="text-[11px] font-bold leading-5 text-red-600 dark:text-red-400">
                          {riskAcknowledgementError}
                        </p>
                        <button
                          type="button"
                          onClick={() => void loadRiskAcknowledgement()}
                          disabled={riskAcknowledgementSaving}
                          className="shrink-0 text-[11px] font-black text-red-600 underline-offset-2 hover:underline disabled:opacity-50 dark:text-red-300"
                        >
                          다시 시도
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {!riskAcknowledgementLoading && (
                <footer className="border-t border-gray-100 bg-slate-50/90 px-5 py-3.5 dark:border-[#2a2a2e] dark:bg-[#141416] sm:px-6">
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={riskConsentChecked}
                      disabled={riskAcknowledgementSaving}
                      onChange={(event) => setRiskConsentChecked(event.target.checked)}
                      className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 accent-seexr-solid disabled:cursor-wait"
                    />
                    <span className="text-[11px] font-medium text-slate-500 dark:text-gray-400">
                      위 내용을 확인하였으며, 이에 동의합니다.
                    </span>
                  </label>
                  <button
                    type="button"
                    disabled={!riskConsentChecked || riskAcknowledgementSaving}
                    onClick={() => void updateRiskAcknowledgement(true)}
                    className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-seexr-solid text-[11px] font-bold text-white transition-colors hover:bg-seexr-solid/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-[#27272b] dark:disabled:text-gray-500"
                  >
                    {riskAcknowledgementSaving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current/25 border-t-current" />}
                    {riskAcknowledgementSaving ? '저장 중…' : '확인'}
                  </button>
                </footer>
              )}
            </section>
          </div>
        )}
        {type === 'risk_assessment' && canViewRiskArtifact && !isEditing && (
          <>
            {riskAssessmentSurvey.isOpen && (
              <RiskAssessmentSurveyPanel
                survey={riskAssessmentSurvey}
                widthClassName={artifactContentWidthClassName}
              />
            )}
            <RiskAssessmentSurveyLoadRetry
              survey={riskAssessmentSurvey}
              widthClassName={artifactContentWidthClassName}
            />
          </>
        )}
      </div>

      <ArtifactDownloadModal
        anchorRect={downloadAnchorRect}
        onClose={() => setDownloadAnchorRect(null)}
        artifactId={resolvedRiskArtifactId}
        type={type}
        title={title}
        xmlContent={withRiskAttachmentFileIds(xmlContent)}
        hasAttachments={(() => {
          if (type === 'risk_assessment') return getRiskAttachmentFileIds(xmlContent).length > 0;
          const block = xmlContent.match(/<attachment_file_ids>([\s\S]*?)<\/attachment_file_ids>/i);
          if (!block) return false;
          return /<id>[\s\S]*?<\/id>/i.test(block[1]);
        })()}
      />


      {/* Image Viewer Modal (Portal to body to escape stacking context) */}
      {viewerState &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setViewerState(null)}
          >
            <div
              className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top-right controls: Download + Close */}
              <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                <button
                  onClick={handleDownload}
                  className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors shadow-lg"
                  title="다운로드"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewerState(null)}
                  className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors shadow-lg"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Left Arrow */}
              {viewerState.index > 0 && (
                <button
                  onClick={() => setViewerState((prev) => prev ? { ...prev, index: prev.index - 1 } : prev)}
                  className="absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors shadow-lg max-md:left-2"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
              )}

              {/* Image — URL/file 두 경로 모두 지원 */}
              {(() => {
                const cur = viewerState.images[viewerState.index];
                const imgClass = "max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain";
                if (cur.kind === 'url') {
                  return (
                    <img
                      src={cur.url}
                      alt="아티팩트 이미지"
                      className={imgClass}
                      onError={(e) => { (e.target as HTMLImageElement).alt = '이미지를 불러올 수 없습니다'; }}
                    />
                  );
                }
                return <AuthImg fileId={cur.fileId} className={imgClass} alt="첨부 이미지" />;
              })()}

              {/* Right Arrow */}
              {viewerState.index < viewerState.images.length - 1 && (
                <button
                  onClick={() => setViewerState((prev) => prev ? { ...prev, index: prev.index + 1 } : prev)}
                  className="absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors shadow-lg max-md:right-2"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </button>
              )}

              {/* Image counter */}
              {viewerState.images.length > 1 && (
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-bold">
                  {viewerState.index + 1} / {viewerState.images.length}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
